import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { configApi } from '../api/config';
import { LandingShell, LandingTop, useLandingPrefix } from '../components/landing/LandingShell';

/**
 * In the order a sceptical reader asks them: what is this, where do its numbers come from, how
 * would it know if they changed, who checks the arithmetic, who writes the articles, and what
 * happens when something is wrong.
 */
const SECTIONS = ['what', 'figures', 'changes', 'checked', 'blog', 'notAdvice'] as const;

/**
 * Who stands behind the numbers, and how they are arrived at.
 *
 * It exists for the reader who lands on an article from a search result and has never heard of
 * the site: a page about money and the law that does not say where its figures come from gives
 * that reader — and the search engine that sent them — no reason to believe it. Everything here
 * describes something the product actually does; there is no mission statement.
 */
export default function AboutPage() {
  const { t } = useTranslation();
  const prefix = useLandingPrefix();

  // Only for the corrections address. The page says everything else without it.
  const { data: config } = useQuery({ queryKey: ['config'], queryFn: configApi.get });

  return (
    <LandingShell>
      <LandingTop>
        <section className="l-hero l-hero--compact">
          <div>
            <p className="l-eyebrow l-eyebrow--onDark">{t('about.eyebrow')}</p>
            <h1>{t('about.title')}</h1>
            <p className="l-hero-sub">{t('about.lead')}</p>
          </div>
        </section>
      </LandingTop>

      <section className="l-section">
        <div className="l-prose">
          {SECTIONS.map((key) => (
            <div key={key}>
              <h2>{t(`about.${key}.title`)}</h2>
              <p>{t(`about.${key}.body`)}</p>
            </div>
          ))}

          <h2>{t('about.corrections.title')}</h2>
          <p>
            {t('about.corrections.body')}
            {/* No address configured, no sentence promising one. */}
            {config?.supportEmail && (
              <>
                {' '}
                {t('about.corrections.writeTo')}{' '}
                <a href={`mailto:${config.supportEmail}`}>{config.supportEmail}</a>.
              </>
            )}
          </p>

          <p className="l-article-cta__actions" style={{ marginTop: '2rem' }}>
            <Link className="l-btn" to={`${prefix}/calculator`}>
              {t('blog.ctaCalculator')}
            </Link>
            <Link className="l-btn l-btn--ghost" to={`${prefix}/blog`}>
              {t('blog.nav')}
            </Link>
          </p>
        </div>
      </section>
    </LandingShell>
  );
}
